import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Download, FileText, ExternalLink } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { generateAnalysisPDF } from '../../utils/pdfGenerator';
import { INVESTMENT_QUESTIONS, BOND_QUESTIONS } from '../../utils/questions';
import { Carousel } from '../ui/Carousel';
import { parseReferences, findMultipleReferences, formatReferenceContent } from '../../utils/referenceExtractor';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const DeepDiveModal = ({
    isOpen,
    onClose,
    question,
    answer,
    reference,
    fileName,
    pdfText,
    extractedText,
    deepAnalysisCache,
    setDeepAnalysisCache,
    analysisData,
    selectedLanguage,
    handleSectionClick,
    analysisMode
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [deepAnalysis, setDeepAnalysis] = useState(null);
    const [error, setError] = useState(null);
    const [referenceDetails, setReferenceDetails] = useState([]);

    // Parse and search for references when modal opens
    useEffect(() => {
        if (isOpen && reference && extractedText) {
            const parsedRefs = parseReferences(reference);
            const foundRefs = findMultipleReferences(parsedRefs, extractedText);
            setReferenceDetails(foundRefs);
            console.log('Reference Details:', foundRefs);
        } else {
            setReferenceDetails([]);
        }
    }, [isOpen, reference, extractedText]);

    useEffect(() => {
        if (isOpen && question) {
            setDeepAnalysis(deepAnalysisCache[question] || null);
            setError(null);
        }
    }, [isOpen, question, deepAnalysisCache]);

    const getDeeperAnalysis = async () => {
        if (!question || !answer) {
            setError('Question and answer are not available.');
            return;
        }

        setIsLoading(true);
        setError(null);
        setDeepAnalysis(null);

        try {
            const response = await axios.post(`${API_BASE_URL}/deeper-analysis`, {
                question,
                answer,
                pdf_text: pdfText || '',
                file_name: fileName,
                target_language: selectedLanguage || 'English',
            }, { timeout: 60000 });

            setDeepAnalysis(response.data);
            setDeepAnalysisCache(prevCache => ({
                ...prevCache,
                [question]: response.data,
            }));
        } catch (err) {
            console.error('Deeper analysis error:', err);
            let errorMessage = 'Failed to get deeper analysis.';
            if (err.response?.status) errorMessage += ` (Status: ${err.response.status})`;
            if (err.message.includes('timeout')) errorMessage += ' Request timed out.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const saveAnalysisToPDF = async () => {
        try {
            if (!analysisData) {
                setError('No analysis data available.');
                return;
            }

            const baseQuestions = analysisMode === 'bond' ? BOND_QUESTIONS : INVESTMENT_QUESTIONS;
            const pdf = await generateAnalysisPDF(
                analysisData,
                deepAnalysisCache,
                fileName || 'Unknown Contract',
                baseQuestions,
                [],
                analysisMode === 'bond' ? 'Bond' : 'Investment'
            );

            const date = new Date().toISOString().split('T')[0];
            const cleanFileName = (fileName || 'contract').replace('.pdf', '').replace(/[^a-zA-Z0-9]/g, '_');
            const pdfFileName = `${cleanFileName}_analysis_${date}.pdf`;
            pdf.save(pdfFileName);
        } catch (error) {
            console.error('Error generating PDF:', error);
            setError('Failed to generate PDF.');
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Deeper Analysis"
            size="lg"
        >
            <div className="space-y-6">
                {/* Header Actions */}
                <div className="flex justify-end">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={saveAnalysisToPDF}
                        className="gap-2"
                    >
                        <Download className="h-4 w-4" />
                        Save to PDF
                    </Button>
                </div>

                {/* Original Question & Answer */}
                <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">{question}</h3>
                    <p className="text-slate-700 pl-4 border-l-4 border-primary-500 leading-relaxed">
                        {answer}
                    </p>
                    {referenceDetails.length > 0 && (
                        <div className="mt-4 pl-4 space-y-3">
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                References ({referenceDetails.length}):
                            </span>
                            <div className="space-y-3">
                                {referenceDetails.map((refDetail, idx) => (
                                    <div key={idx} className="bg-white rounded-lg border border-slate-200 p-3 space-y-2">
                                        <div className="flex items-center gap-2">
                                            <Badge variant={refDetail.found ? "success" : "warning"} className="text-xs">
                                                {refDetail.found ? '✓ Found' : '⚠ Not Found'}
                                            </Badge>
                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleSectionClick && handleSectionClick(refDetail.reference);
                                                }}
                                                className="text-sm text-primary-600 hover:text-primary-700 font-medium hover:underline flex items-center gap-1"
                                            >
                                                <FileText className="h-3 w-3" />
                                                {refDetail.reference}
                                                <ExternalLink className="h-3 w-3" />
                                            </button>
                                        </div>
                                        {refDetail.found && refDetail.content && (
                                            <p className="text-xs text-slate-600 leading-relaxed pl-2 border-l-2 border-slate-200">
                                                {formatReferenceContent(refDetail.content, 300)}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Generate Button or Loading */}
                {!deepAnalysis && (
                    <div className="flex justify-center py-8">
                        <Button
                            onClick={getDeeperAnalysis}
                            disabled={isLoading}
                            size="lg"
                            className="w-full sm:w-auto"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                    Generating Analysis...
                                </>
                            ) : (
                                'Get Deeper Analysis Questions'
                            )}
                        </Button>
                    </div>
                )}

                {/* Error Display */}
                {error && (
                    <div className="bg-danger-50 border border-danger-200 text-danger-700 p-4 rounded-lg text-sm">
                        {error}
                    </div>
                )}

                {/* Results */}
                {deepAnalysis && (
                    <div className="space-y-6 animate-fade-in">
                        <h4 className="text-xl font-semibold text-slate-900">Generated Questions</h4>
                        <Carousel
                            items={deepAnalysis.generated_questions}
                            renderItem={(item, index) => (
                                <div className="px-1">
                                    <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
                                        <div className="flex gap-3 mb-3">
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-sm font-bold">
                                                {index + 1}
                                            </span>
                                            <p className="font-semibold text-slate-900">{item.question}</p>
                                        </div>

                                        <p className="text-slate-700 pl-9 border-l-2 border-slate-100 mb-3">
                                            {item.answer}
                                        </p>

                                        {item.section_reference &&
                                            item.section_reference !== "N/A" &&
                                            item.section_reference !== "Not specified in available sections" && (
                                                <div className="pl-9 flex items-center gap-2">
                                                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                                        Reference:
                                                    </span>
                                                    <button
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleSectionClick && handleSectionClick(item.section_reference);
                                                        }}
                                                        className="text-sm text-primary-600 hover:text-primary-700 font-medium hover:underline flex items-center gap-1"
                                                    >
                                                        <FileText className="h-3 w-3" />
                                                        {item.section_reference}
                                                    </button>
                                                </div>
                                            )}
                                    </div>
                                </div>
                            )}
                        />
                    </div>
                )}
            </div>
        </Modal >
    );
};
